/**
 * Colar print na seção Anexos (spec 066, D2). Puro de propósito: a guarda
 * apps/api/src/shared/attachment-paste.test.ts confere o nome do print e o que
 * conta como colagem de anexo.
 */

/** O alvo da colagem, só o que a regra olha (o DOM no navegador, um falso no teste). */
export interface PasteTarget {
  closest(selector: string): unknown;
}

/**
 * Onde o Ctrl+V é do campo, e não do anexo: campo de texto, editor
 * (contenteditable) e qualquer janela aberta por cima do ticket.
 */
export const PASTE_OWNED_BY_FIELD =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="dialog"], [role="alertdialog"]';

export function pasteBelongsToAttachments(target: PasteTarget | null): boolean {
  if (target === null) return true;
  return !target.closest(PASTE_OWNED_BY_FIELD);
}

const pad = (n: number) => String(n).padStart(2, '0');

/** "print-2026-10-27-14h05m09s.png", na hora local de quem colou */
export function printFileName(at: Date, extension = 'png'): string {
  const day = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  const time = `${pad(at.getHours())}h${pad(at.getMinutes())}m${pad(at.getSeconds())}s`;
  return `print-${day}-${time}.${extension}`;
}

/** O navegador chama todo print de "image.png": esse nome não diz nada. */
const GENERIC_IMAGE_NAME = /^image\.(png|jpe?g|gif|webp|bmp)$/i;

export interface PastedFile {
  name: string;
  type: string;
}

/**
 * O nome com que o arquivo colado vai ser anexado: print sem nome próprio vira
 * `print-…`; arquivo copiado do computador mantém o nome que tem.
 */
export function pastedFileName(file: PastedFile, at: Date): string {
  if (file.name && !GENERIC_IMAGE_NAME.test(file.name)) return file.name;
  const ext = file.type.startsWith('image/')
    ? file.type.slice('image/'.length).replace('jpeg', 'jpg')
    : 'png';
  // o print do Windows e do macOS chega como png; um sufixo estranho vira png
  return printFileName(at, /^[a-z0-9]{2,5}$/.test(ext) ? ext : 'png');
}

/**
 * Spec 068 (D4): o Ctrl+V DENTRO do campo da Comunicação. Sem arquivo na área
 * de transferência, a colagem é texto e fica com o campo (null). Com arquivo,
 * são anexos, com estes nomes, na ordem. Dois prints colados no mesmo segundo
 * teriam o mesmo nome; o segundo ganha `-2`, o terceiro `-3`.
 */
export function pastedAttachmentNames(files: readonly PastedFile[], at: Date): string[] | null {
  if (files.length === 0) return null;
  const seen = new Map<string, number>();
  return files.map((file) => {
    const name = pastedFileName(file, at);
    const n = (seen.get(name) ?? 0) + 1;
    seen.set(name, n);
    if (n === 1) return name;
    const dot = name.lastIndexOf('.');
    return dot > 0 ? `${name.slice(0, dot)}-${n}${name.slice(dot)}` : `${name}-${n}`;
  });
}
